const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";

export const DEMO_ROLE_IDS = new Set([3, 4]);

export function getStoredUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
}

export function isDemoMode() {
  const user = getStoredUser();
  return DEMO_ROLE_IDS.has(Number(user?.role_id));
}

export function getFallbackSectionId(sectionId) {
  if (sectionId) return Number(sectionId);

  const user = getStoredUser();

  return user?.section_id ? Number(user.section_id) : null;
}

export function isExternalURL(url) {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

export function resolveMediaUrl(url, fallback = "/images/asso_placeholder.webp") {
  if (!url) return fallback;

  const value = String(url).trim();

  if (
    isExternalURL(value) ||
    value.startsWith("data:") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  if (value.startsWith("/")) {
    return `${API_BASE}${value}`;
  }

  return value;
}

export function normalizeText(value = "") {
  return String(value)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

export function truncateText(value = "", max = 120) {
  const clean = String(value || "").replace(/\s+/g, " ").trim();

  if (clean.length <= max) return clean;

  return `${clean.slice(0, max).trim()}...`;
}

export function getAlbumDate(album) {
  return album?.createdAt || album?.created_at || album?.updatedAt || null;
}

export function formatDate(dateValue) {
  if (!dateValue) return "—";

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

export function getPhotos(album) {
  return Array.isArray(album?.photos) ? album.photos : [];
}

export function getPhotosCount(album) {
  return getPhotos(album).length;
}

export function getAlbumCover(album) {
  const photos = getPhotos(album);
  const firstActive = photos.find((photo) => photo?.is_active) || photos[0];

  return firstActive?.photo_url || "";
}

export function sortAlbums(albums = []) {
  return [...albums].sort((a, b) => {
    const dateA = new Date(getAlbumDate(a) || 0).getTime();
    const dateB = new Date(getAlbumDate(b) || 0).getTime();

    return dateB - dateA;
  });
}

export function filterAlbums(albums = [], searchTerm = "", statusFilter = "all") {
  const q = normalizeText(searchTerm.trim());

  return albums.filter((album) => {
    const isActive = Boolean(album?.is_active);

    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && isActive) ||
      (statusFilter === "inactive" && !isActive);

    const searchable = normalizeText(
      [album?.titre, album?.description, album?.section?.nom_section]
        .filter(Boolean)
        .join(" ")
    );

    const matchesSearch = !q || searchable.includes(q);

    return matchesStatus && matchesSearch;
  });
}

export function getAlbumsStats(albums = []) {
  const total = albums.length;
  const active = albums.filter((album) => album.is_active).length;
  const inactive = total - active;
  const photos = albums.reduce((sum, album) => sum + getPhotosCount(album), 0);

  return {
    total,
    active,
    inactive,
    photos,
  };
}

export const emptyAlbumForm = {
  titre: "",
  description: "",
  is_active: true,
};

export function mapAlbumToForm(album) {
  return {
    titre: album?.titre || "",
    description: album?.description || "",
    is_active: Boolean(album?.is_active),
  };
}